import React, { useState } from "react";
import { SvgIcon, Stack } from "@mui/material";
import {
  AddShoppingCartOutlined,
  RefreshOutlined,
  StarBorderOutlined,
} from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";

import {
  IconButton,
  PurchaseMenu,
  ReplenishMenu,
  Tooltip,
} from "../../../../components";

import { ILookup } from "../../../../features/lookups/lookups.types";
import { updateProduct } from "../../../../services/apiV2";
import { useAppDispatch } from "../../../../store/hooks";
import { Notifications } from "../../../../constants";
import { updateLookupsByAsin } from "../../../../features/lookups/lookups.slice";

interface IActionBtnGroupProps {
  item: ILookup;
}

const ActionBtnGroup: React.FC<IActionBtnGroupProps> = ({
  item: { salePrice, costPrice, asin, isPurchased, isReplenished, isFavorite },
}) => {
  const dispatch = useAppDispatch();

  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState<boolean>(false);
  const [purchaseMenuEl, setPurchaseMenuEl] = useState<null | HTMLElement>(
    null,
  );
  const [replenishMenuEl, setReplenishMenuEl] = useState<null | HTMLElement>(
    null,
  );
  const isPurchaseMenuOpened = Boolean(purchaseMenuEl);
  const isReplenishMenuOpened = Boolean(replenishMenuEl);

  const handleFavoriteClick = () => {
    setIsUpdatingFavorite(true);
    updateProduct({ asin, isFavorite: !isFavorite })
      .then(() => {
        dispatch(updateLookupsByAsin({ asin, isFavorite: !isFavorite }));
      })
      .catch((e) => {
        console.error(e);
        enqueueSnackbar(Notifications.PA_UPDATE_ERROR, { variant: "error" });
      })
      .finally(() => {
        setIsUpdatingFavorite(false);
      });
  };

  const handlePurchase = () => {
    dispatch(updateLookupsByAsin({ asin, isPurchased: true }));
  };

  const handleReplenish = () => {
    dispatch(updateLookupsByAsin({ asin, isReplenished: true }));
  };

  return (
    <Stack direction="row" gap="8px">
      <IconButton
        size="small"
        color={isPurchased ? "secondary" : "default"}
        id="purchase-button"
        aria-controls={isPurchaseMenuOpened ? "purchase-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isPurchaseMenuOpened ? "true" : undefined}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setPurchaseMenuEl(event.currentTarget)
        }
      >
        <Tooltip title={"Add purchased quantity"}>
          <SvgIcon
            component={AddShoppingCartOutlined}
            sx={{ fontSize: "16px" }}
          />
        </Tooltip>
      </IconButton>
      <IconButton
        size="small"
        color={isReplenished ? "secondary" : "default"}
        id="replenish-button"
        aria-controls={isReplenishMenuOpened ? "replenish-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isReplenishMenuOpened ? "true" : undefined}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setReplenishMenuEl(event.currentTarget)
        }
      >
        <Tooltip title={"Add replenished quantity"}>
          <SvgIcon component={RefreshOutlined} sx={{ fontSize: "18px" }} />
        </Tooltip>
      </IconButton>
      <IconButton
        loading={isUpdatingFavorite}
        onClick={handleFavoriteClick}
        size="small"
        color={isFavorite ? "secondary" : "default"}
      >
        <Tooltip
          title={isFavorite ? "Remove from favorite" : "Add to favorite"}
        >
          <SvgIcon component={StarBorderOutlined} sx={{ fontSize: "18px" }} />
        </Tooltip>
      </IconButton>
      <PurchaseMenu
        open={isPurchaseMenuOpened}
        anchorEl={purchaseMenuEl}
        meta={{ asin, salePrice, costPrice }}
        onClose={() => setPurchaseMenuEl(null)}
        onPurchase={handlePurchase}
      />
      <ReplenishMenu
        open={isReplenishMenuOpened}
        anchorEl={replenishMenuEl}
        meta={{ asin, salePrice, costPrice }}
        onClose={() => setReplenishMenuEl(null)}
        onReplenish={handleReplenish}
      />
    </Stack>
  );
};

export default ActionBtnGroup;
